import { FC, memo, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { isMobile } from 'react-device-detect';

import { AppTracker, ShareType } from 'types';
import { LoggerService, tracker } from 'services';
import { useConfirmation, useToast } from 'hooks';
import { ConfirmPanel } from 'containers';
import { useLiveEvent } from 'containers/LiveEvent';
import { Divider, TextArea } from 'components';
import { CheckedIcon, CopyIcon, RotateIcon, ThreeDotsIcon } from 'components/Icon';

import styles from './InviteToStage.ShareLink.module.scss';

export type ShareLinkProps = {
  text: string;
  url: string;
  onReset?(): void | Promise<void>;
};

const ShareLink: FC<ShareLinkProps> = ({ text, url, onReset }) => {
  const toast = useToast();
  const liveEvent = useLiveEvent();
  const confirmation = useConfirmation<'RESET_INVITATION'>();
  const [resetting, setResetting] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleOnCopy = () => {
    tracker.shareEventLink(liveEvent.eventCode, ShareType.COPY, AppTracker.ShareSource.SHARE_INVITATION_LINK_PANEL);

    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2_000);
  };

  const handleOnShare = () => {
    tracker.shareEventLink(liveEvent.eventCode, ShareType.NATIVE, AppTracker.ShareSource.SHARE_INVITATION_LINK_PANEL);

    try {
      navigator.share({ text, url });
    } catch (e) {
      alert('Not supported');
    }
  };

  const handleOnReset = () => {
    confirmation.open('RESET_INVITATION');
  };

  const handleOnResetCancelled = () => {
    confirmation.close();
  };

  const handleOnResetSubmitted = async () => {
    setResetting(true);
    try {
      await onReset?.();
      confirmation.close();
    } catch (e) {
      LoggerService.log('RESET_INVITATION', e);
      if (e instanceof Error) {
        toast.error(e, { id: 'RESET_INVITATION' });
      }
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.invitation}>
          <div className={styles.header}>
            <h3 className={styles.label}>Invitation</h3>
            <button type="button" className={styles.reset} disabled={resetting} onClick={handleOnReset}>
              <RotateIcon />
              <span>Reset invitation</span>
            </button>
          </div>
          <TextArea value={text} minRows={4} />
        </div>

        <CopyToClipboard text={text}>
          <button type="button" className={styles.copy} onClick={handleOnCopy}>
            {copied ? <CheckedIcon /> : <CopyIcon />}
            <span>Copy invitation</span>
          </button>
        </CopyToClipboard>

        {isMobile && (
          <>
            <Divider text="Or" className={styles.separator} />
            <button type="button" className={styles.more} onClick={handleOnShare}>
              <ThreeDotsIcon />
              <span>More options</span>
            </button>
          </>
        )}
      </div>

      <ConfirmPanel
        show={confirmation.state.isOpen}
        title="Do you want to reset the invitation?"
        name="RESET_INVITATION"
        message="The invitation link won't be accessible anymore"
        submitText="Reset invitation"
        onCancel={handleOnResetCancelled}
        onSubmit={handleOnResetSubmitted}
      />
    </>
  );
};

const memoizedShareLink = memo(ShareLink);

export default memoizedShareLink;
